import { useEffect, useState } from "react";
import { Card, Stack, Badge, Button, TextInput, Group, Select } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { IconPaperclip, IconPlus, IconSearch } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useInfiniteResource, useResource } from "../../../shared/api";
import { PageHeader, MoneyDisplay, EmptyState, InfiniteTable, formatDateTime } from "../../../shared/ui";
import { SupplierInvoiceFormModal } from "../components/SupplierInvoiceFormModal";
import { SupplierInvoiceDetailModal } from "../components/SupplierInvoiceDetailModal";

// DRAFT = fatura oluşturuldu ama henüz "İşle" ile cariye işlenmedi →
// "İşlenmeyi Bekliyor" (turuncu, aksiyon gerektiğini vurgular). GR/PO ile aynı
// desen: bekleyen onay/işlem adımı turuncu, bir sonraki adım mavi, biten yeşil.
const STATUS_COLOR: Record<string, string> = {
  DRAFT: "orange", POSTED: "blue", PAID: "green", PARTIALLY_PAID: "yellow", CANCELLED: "red",
};
const STATUS_LABEL: Record<string, string> = {
  DRAFT: "İşlenmeyi Bekliyor", POSTED: "İşlendi", PAID: "Ödendi",
  PARTIALLY_PAID: "Kısmen Ödendi", CANCELLED: "İptal",
};

export function SupplierInvoicesPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [supplierFilter, setSupplierFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  // Vade filtresi: "overdue" (geçmiş) | "upcoming" (7 gün) | "has_due" | null.
  const [dueFilter, setDueFilter] = useState<string | null>(null);
  const [dueFrom, setDueFrom] = useState<Date | null>(null);
  const [dueTo, setDueTo] = useState<Date | null>(null);
  const isoDate = (d: Date | null) =>
    d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` : null;
  const filters: Record<string, any> = {
    ...(supplierFilter ? { supplier: supplierFilter } : {}),
    ...(statusFilter ? { status: statusFilter } : {}),
    ...(dueFilter ? { due: dueFilter } : {}),
    ...(dueFrom ? { due_from: isoDate(dueFrom) } : {}),
    ...(dueTo ? { due_to: isoDate(dueTo) } : {}),
  };
  const {
    items: list, total, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage,
  } = useInfiniteResource<any>("supplier-invoices", {
    ...(search.trim() ? { search: search.trim() } : {}),
    ...(Object.keys(filters).length ? { filters } : {}),
  });
  const { data: suppliersData } = useResource<any>("suppliers");
  const suppliers: any[] = suppliersData?.results || suppliersData || [];
  const [formOpen, setFormOpen] = useState(false);
  const [detail, setDetail] = useState<any | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["supplier-invoices"] });

  // Satın Alma Özeti'nden "vadesi geçmiş fatura" satırına tıklanınca buraya
  // highlight_id ile gelinir (admin:navigate → sessionStorage). Hedef kaydın
  // listede olması için vade filtresini "overdue"a çek; kayıt düşünce detay
  // modalını bir kez otomatik aç.
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const [autoOpened, setAutoOpened] = useState(false);
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("admin:nav:supplier-invoices");
      if (!raw) return;
      sessionStorage.removeItem("admin:nav:supplier-invoices");
      const params = JSON.parse(raw);
      if (typeof params.highlight_id === "string") {
        setHighlightId(params.highlight_id);
        setDueFilter("overdue");
      }
    } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    if (autoOpened || detail || !highlightId) return;
    const match = list.find((i) => i.id === highlightId);
    if (match) {
      setDetail(match);
      setAutoOpened(true);
    }
  }, [list, highlightId, autoOpened, detail]);

  return (
    <Stack gap="md">
      <PageHeader
        title="Tedarikçi Faturaları"
        subtitle={`${total} fatura`}
        actions={
          <Button leftSection={<IconPlus size={16} />} onClick={() => setFormOpen(true)}>
            Yeni Fatura
          </Button>
        }
      />
      <Group>
        <TextInput
          placeholder="Fatura no veya tedarikçi adıyla ara…"
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          style={{ flex: 1, maxWidth: 420 }}
        />
        <Select
          placeholder="Tüm tedarikçiler"
          clearable
          searchable
          value={supplierFilter}
          onChange={setSupplierFilter}
          data={suppliers.map((s) => ({ value: s.id, label: s.name }))}
          style={{ width: 220 }}
        />
        <Select
          placeholder="Tüm durumlar"
          clearable
          value={statusFilter}
          onChange={setStatusFilter}
          data={Object.entries(STATUS_LABEL).map(([value, label]) => ({ value, label }))}
          style={{ width: 170 }}
        />
        <Select
          placeholder="Vade"
          clearable
          value={dueFilter}
          onChange={setDueFilter}
          data={[
            { value: "overdue", label: "Vadesi geçmiş" },
            { value: "upcoming", label: "Yaklaşan (7 gün)" },
            { value: "has_due", label: "Vadeli (tümü)" },
          ]}
          style={{ width: 160 }}
        />
        <DateInput
          placeholder="Vade başlangıç" clearable valueFormat="DD.MM.YYYY"
          value={dueFrom} onChange={(d) => setDueFrom(d as Date | null)} style={{ width: 150 }}
        />
        <DateInput
          placeholder="Vade bitiş" clearable valueFormat="DD.MM.YYYY"
          value={dueTo} onChange={(d) => setDueTo(d as Date | null)} style={{ width: 150 }}
        />
      </Group>
      <Card padding="sm" radius="md" withBorder>
        <InfiniteTable
          rows={list}
          rowKey={(i) => i.id}
          isLoading={isLoading}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          onRowClick={(i) => setDetail(i)}
          emptyContent={<EmptyState title="Fatura yok" />}
          columns={[
            { header: "Fatura No", render: (i) => (
              <span style={{ fontWeight: 600 }}>
                {i.invoice_number}
                {i.has_attachment && <IconPaperclip size={14} style={{ marginLeft: 6, verticalAlign: "middle", opacity: 0.6 }} />}
              </span>
            ) },
            { header: "Tedarikçi", render: (i) => i.supplier_name },
            // Tek satır: saatli kayıt zamanı (invoice_date DateField saatsiz; ayrı
            // tarih satırı tekrar olurdu).
            { header: "Tarih", render: (i) => formatDateTime(i.created_at) },
            { header: "Vade", render: (i) => (i.due_date ? new Date(i.due_date).toLocaleDateString("tr-TR") : "-") },
            { header: "Toplam", align: "right", render: (i) => <MoneyDisplay minor={i.total_minor} fw={600} /> },
            { header: "Durum", render: (i) => <Badge color={STATUS_COLOR[i.status]}>{STATUS_LABEL[i.status] || i.status}</Badge> },
          ]}
        />
      </Card>

      <SupplierInvoiceFormModal opened={formOpen} onClose={() => { setFormOpen(false); refresh(); }} />
      <SupplierInvoiceDetailModal
        invoice={detail}
        onClose={() => setDetail(null)}
        onChanged={refresh}
      />
    </Stack>
  );
}
